import { normalizeProfile } from './profile'
import { normalizeLongEdge } from './upscaleWorkflow'
import { DEFAULT_UPSCALE_SETTINGS } from '../defaults'
import type { AppProfile, InspectorTab, LoraScope, UpscaleSettings, UpscaleTool, WorkspaceMode } from '../types'

export const DRAFT_IDLE_SAVE_MS = 120_000

export interface WorkDraftState {
  profile: AppProfile
  profileName: string
  batch: number
  target: string
  tab: InspectorTab
  loraScope: LoraScope
  autoPair: boolean
  startImageId: string
  endModes: Record<string, 'start' | 'image'>
  lastUsedSeed: number | null
  workspaceMode: WorkspaceMode
  upscaleTool: UpscaleTool
  upscaleSettings: UpscaleSettings
  compactResultPreview: boolean
}

export interface StoredWorkDraft extends WorkDraftState {
  version: 1
  savedAt: number
}

const key = (identity: string) => `mugen2.work-draft.${identity || 'local'}`

export function readWorkDraft(identity: string): StoredWorkDraft | null {
  try {
    const raw = JSON.parse(localStorage.getItem(key(identity)) || 'null') as Record<string, unknown> | null
    if (!raw || typeof raw !== 'object') return null
    const rawEndModes = raw.endModes && typeof raw.endModes === 'object'
      ? raw.endModes as Record<string, unknown>
      : {}
    const endModes = Object.fromEntries(Object.entries(rawEndModes)
      .filter((entry): entry is [string, 'start' | 'image'] => entry[1] === 'start' || entry[1] === 'image'))
    const rawScope = raw.loraScope && typeof raw.loraScope === 'object'
      ? raw.loraScope as Record<string, unknown>
      : {}
    const loraScope: LoraScope = rawScope.kind === 'segment' && typeof rawScope.segmentId === 'string'
      ? { kind: 'segment', segmentId: rawScope.segmentId }
      : { kind: 'global' }
    const tab: InspectorTab = raw.tab === 'lora' || raw.tab === 'settings' ? raw.tab : 'result'
    const seed = Number(raw.lastUsedSeed)
    const profile = normalizeProfile(raw.profile)
    const rawUpscale = raw.upscaleSettings && typeof raw.upscaleSettings === 'object'
      ? raw.upscaleSettings as Record<string, unknown>
      : {}
    const bounded = (value: unknown, fallback: number, min: number, max: number) => {
      const number = Number(value)
      return Number.isFinite(number) ? Math.max(min, Math.min(max, number)) : fallback
    }
    return {
      version: 1,
      savedAt: Number.isFinite(Number(raw.savedAt)) ? Number(raw.savedAt) : 0,
      profile,
      profileName: typeof raw.profileName === 'string' ? raw.profileName : '',
      batch: Math.max(1, Math.min(99, Number.isFinite(Number(raw.batch)) ? Number(raw.batch) : 1)),
      target: typeof raw.target === 'string' && raw.target ? raw.target : 'auto',
      tab,
      loraScope,
      autoPair: raw.autoPair !== false,
      startImageId: typeof raw.startImageId === 'string' ? raw.startImageId : '',
      endModes,
      lastUsedSeed: raw.lastUsedSeed !== null && Number.isSafeInteger(seed) ? seed : null,
      workspaceMode: raw.workspaceMode === 'upscale' || raw.workspaceMode === 'simple' || raw.workspaceMode === 'multi'
        ? raw.workspaceMode
        : profile.mode,
      upscaleTool: raw.upscaleTool === 'concat' ? 'concat' : 'enhance',
      upscaleSettings: {
        upscaleModel: typeof rawUpscale.upscaleModel === 'string' && rawUpscale.upscaleModel ? rawUpscale.upscaleModel : DEFAULT_UPSCALE_SETTINGS.upscaleModel,
        scale: bounded(rawUpscale.scale, DEFAULT_UPSCALE_SETTINGS.scale, 1, 4),
        scaleMode: rawUpscale.scaleMode === 'longEdge' ? 'longEdge' : 'factor',
        longEdge: normalizeLongEdge(rawUpscale.longEdge ?? DEFAULT_UPSCALE_SETTINGS.longEdge),
        targetFps: bounded(rawUpscale.targetFps, DEFAULT_UPSCALE_SETTINGS.targetFps, 1, 120),
        enhanceSource: rawUpscale.enhanceSource === 'h3' ? 'h3' : 'wan',
        enhanceLoop: rawUpscale.enhanceLoop !== false,
        preserveAudio: rawUpscale.preserveAudio !== false,
        concatFps: bounded(rawUpscale.concatFps, DEFAULT_UPSCALE_SETTINGS.concatFps, 1, 120),
        concatCrf: bounded(rawUpscale.concatCrf, DEFAULT_UPSCALE_SETTINGS.concatCrf, 0, 51),
      },
      compactResultPreview: raw.compactResultPreview !== false,
    }
  } catch {
    return null
  }
}

export function writeWorkDraft(identity: string, state: WorkDraftState): void {
  localStorage.setItem(key(identity), JSON.stringify({ ...state, version: 1, savedAt: Date.now() } satisfies StoredWorkDraft))
}
